// creator-assets.ts — §L1. Client side of chat attachments: upload a file the
// creator picked in the build/edit chat to POST /v1/creator/assets and shape
// the result into the wire format /v1/generate expects.
//
// Images come back with a DURABLE public URL (they become app content);
// sheets come back with their parsed rows (capped server-side); PDFs come
// back as a private storage key the gateway inlines at generate time.

export interface CreatorAssetWire {
  kind: 'image' | 'rows' | 'pdf';
  url?: string;
  key?: string;
  name: string;
  columns?: string[];
  rows?: Record<string, string>[];
}

export interface UploadedAsset extends CreatorAssetWire {
  bytes: number;
  rowCount?: number;
}

export class AssetUploadError extends Error {
  constructor(message: string, public code: string) { super(message); }
}

export async function uploadCreatorAsset(file: File): Promise<UploadedAsset> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch('/v1/creator/assets', { method: 'POST', credentials: 'include', body: form });
  const j = (await res.json().catch(() => ({}))) as {
    ok?: boolean; error?: string; message?: string;
    asset?: { key: string; name: string; kind: 'image' | 'rows' | 'pdf'; bytes: number; url?: string | null; columns?: string[]; rows?: Record<string, string>[]; rowCount?: number };
  };
  if (!res.ok || !j.ok || !j.asset) {
    throw new AssetUploadError(j.message ?? "Couldn't attach that file. Try again.", j.error ?? `http_${res.status}`);
  }
  const a = j.asset;
  return {
    kind: a.kind,
    name: a.name,
    bytes: a.bytes,
    ...(a.kind === 'image' ? { url: a.url ?? undefined } : {}),
    ...(a.kind === 'pdf' ? { key: a.key } : {}),
    ...(a.kind === 'rows' ? { columns: a.columns, rows: a.rows, rowCount: a.rowCount, key: a.key } : {})
  };
}

/** Strip client-only fields down to the /v1/generate wire shape. */
export function toWire(assets: UploadedAsset[]): CreatorAssetWire[] {
  return assets.map((a) => ({
    kind: a.kind,
    name: a.name,
    ...(a.kind === 'image' ? { url: a.url } : {}),
    ...(a.kind === 'pdf' ? { key: a.key } : {}),
    ...(a.kind === 'rows' ? { columns: a.columns, rows: a.rows } : {})
  }));
}
